'use client';

import { Transition } from '@headlessui/react';
import { useNavigationLoading } from '@lib/hooks/use-navigation-loading';
import { convertToLocale } from '@lib/util/money';
import { CartItemCard } from '@modules/cart/components/cart-item-card';
import { Button } from '@modules/common/components/button';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import { EmptyBasket, ShoppingCart } from '@modules/common/icons';
import { usePathname } from 'next/navigation';
import { Fragment, useEffect, useRef, useState } from 'react';
import type { CartDropdownVM } from 'types/view-models';

const CartDropdown = ({
  cart: cartState,
  mutationVersion = 0,
}: {
  cart?: CartDropdownVM | null;
  /** Increments only when the current user adds/updates/removes a cart item
   * (see `CartProvider`'s `CART_CHANGED_EVENT` listener). Drives the
   * auto-open effect below instead of diffing item count — `cart` can also
   * change via `LayoutDataLoader`'s per-navigation background refresh (e.g.
   * the cart changed on another device), which looks identical from the
   * item count alone but shouldn't pop the dropdown open. */
  mutationVersion?: number;
}) => {
  const [cartDropdownOpen, setCartDropdownOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { navigate, isPending } = useNavigationLoading();

  const open = () => setCartDropdownOpen(true);
  const close = () => setCartDropdownOpen(false);

  const totalItems =
    cartState?.items?.reduce((acc, item) => {
      return acc + item.quantity;
    }, 0) || 0;

  const subtotal = cartState?.item_subtotal ?? 0;
  const mutationVersionRef = useRef(mutationVersion);

  const timedOpen = () => {
    open();
    timerRef.current = setTimeout(close, 5000);
  };

  const openAndCancel = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    open();
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const pathname = usePathname();

  // biome-ignore lint/correctness/useExhaustiveDependencies: timedOpen depends on state that would cause infinite re-renders; we only want this to fire when mutationVersion or pathname changes
  useEffect(() => {
    if (mutationVersion === mutationVersionRef.current) {
      return;
    }
    mutationVersionRef.current = mutationVersion;

    if (!pathname.includes('/basket')) {
      timedOpen();
    }
  }, [mutationVersion, pathname]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hover handlers are progressive enhancement for the cart preview; keyboard users navigate via the cart link
    <div
      className="relative h-full"
      onMouseEnter={openAndCancel}
      onMouseLeave={close}
    >
      <LocalizedClientLink
        className="relative h-full flex items-center text-white hover:text-fg-accent-inverse-soft transition-colors duration-200"
        href="/basket"
        prefetch={false}
        data-testid="nav-cart-link"
        aria-label={`Shopping basket with ${totalItems} items`}
      >
        <ShoppingCart size="44" aria-hidden="true" />
        {totalItems > 0 && (
          <span
            className="absolute top-2 right-0 flex items-center justify-center w-[18px] h-[18px] text-[10px] font-regular leading-none text-fg-neutral-strong bg-surface-interactive-default rounded-full"
            aria-live="polite"
            translate="no"
          >
            {totalItems > 99 ? '99+' : totalItems}
          </span>
        )}
      </LocalizedClientLink>
      <Transition
        show={cartDropdownOpen}
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <div
          className="hidden desktop:block absolute pt-4 top-[64px] right-0 bg-white border border-border-semantic-subtle shadow-lg w-[420px] max-w-[calc(100vw-2rem)] text-ui-fg-base rounded-b-lg"
          data-testid="nav-cart-dropdown"
        >
          {cartState?.items?.length ? (
            <>
              <div className="overflow-y-scroll max-h-[402px] px-4 py-px flex flex-col gap-y-6 no-scrollbar">
                {cartState.items
                  .sort((a, b) => {
                    return (a.created_at ?? '') > (b.created_at ?? '') ? -1 : 1;
                  })
                  .map((item) => (
                    <div key={item.id} data-testid="cart-item">
                      <CartItemCard
                        item={item}
                        currencyCode={cartState.currency_code}
                        compact
                      />
                    </div>
                  ))}
              </div>
              <div className="p-4 flex flex-col gap-y-4 typography-paragraph-xs">
                <div className="flex items-center justify-between">
                  <span className="text-ui-fg-base font-semibold">
                    Subtotal
                  </span>
                  <span
                    className="typography-subheading font-semibold"
                    data-testid="cart-subtotal"
                    data-value={subtotal}
                    translate="no"
                  >
                    {convertToLocale({
                      amount: subtotal,
                      currency_code: cartState.currency_code,
                    })}
                  </span>
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full"
                  isLoading={isPending}
                  data-testid="go-to-cart-button"
                  onClick={() => navigate('/basket')}
                >
                  Go to Basket
                </Button>
              </div>
            </>
          ) : (
            <div>
              <div className="flex py-16 flex-col gap-y-4 items-center justify-center">
                <EmptyBasket
                  size="32"
                  className="text-fg-interactive-strong"
                  aria-hidden="true"
                />
                <span>Your basket is empty</span>
                <div>
                  <Button
                    variant="primary"
                    isLoading={isPending}
                    onClick={() => {
                      close();
                      navigate('/search');
                    }}
                  >
                    Explore Products
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Transition>
    </div>
  );
};

export default CartDropdown;
